import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, startAfter, getDocs, writeBatch, doc, updateDoc, where, Timestamp, getDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ChevronLeft, CheckCheck, Trash2, AlertTriangle, Heart, MessageCircle, Zap, Globe, User, Megaphone, Sparkles, BarChart2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getAnonymousName, getAvatarForShadow } from '../utils/shadowIdentity';
import { useIdentity } from '../context/IdentityContext';
import { useNavigationCache } from '../context/NavigationCacheContext';
import sparkLogo from '../assets/spark-logo.png';

const DELETED_IDS_KEY = 'activity_deleted_ids_store';

const getDeletedIds = () => {
    try {
        const json = localStorage.getItem(DELETED_IDS_KEY);
        return json != null ? JSON.parse(json) : [];
    } catch (e) { return []; }
};

const addDeletedId = (id) => {
    try {
        const current = getDeletedIds();
        if (!current.includes(id)) {
            const updated = [...current, id];
            localStorage.setItem(DELETED_IDS_KEY, JSON.stringify(updated));
        }
    } catch (e) {}
};

// Row component
const NotificationRow = memo(({ item, userProfile, postData, onDelete, onPress }) => {
    let message = '';
    let subMessage = '';
    let senderName = 'Someone';
    let AvatarComponent = null;
    let IconBadge = Zap;
    let badgeColor = '#4488FF';
    let PostPreviewComponent = null;

    const isSocial = ['post_like', 'post_comment', 'post_comment_reply', 'new_follower', 'post_comment_nested', 'new_post', 'new_follower_request', 'request_accepted', 'post_mention', 'comment_mention'].includes(item.type) 
                     || (item.type === 'poll_vote' && !item.isConfession);

    if (isSocial) {
        senderName = userProfile?.username || userProfile?.name || 'Someone'; 
        if (userProfile?.photoURL) {
            AvatarComponent = <img src={userProfile.photoURL} className="w-12 h-12 rounded-full border border-[#333] object-cover" alt="" />;
        } else {
            AvatarComponent = <div className="w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#333] flex items-center justify-center"><User size={24} color="#888" /></div>;
        }
        if ((item.type.startsWith('post_') || item.type === 'new_post' || (item.type === 'poll_vote' && !item.isConfession)) && item.postId) {
            if (postData?.imageUrl) PostPreviewComponent = <img src={postData.imageUrl} className="w-11 h-11 rounded-lg border border-[#222] object-cover" alt="" />;
        }
    } else {
        if (item.type === 'new_confession') senderName = getAnonymousName(item.fromUserId);
        else senderName = item.type.includes('live') ? 'Spark' : getAnonymousName(item.fromUserId);
        
        if (senderName === 'Spark') {
            AvatarComponent = (
                <div className="w-12 h-12 rounded-full bg-black border border-[#333] flex items-center justify-center p-2.5">
                    <img src={sparkLogo} className="w-full h-full object-contain opacity-90" alt="Spark" />
                </div>
            );
        } else {
            const avatarUrl = getAvatarForShadow(senderName);
            AvatarComponent = <img src={avatarUrl} className="w-12 h-12 rounded-full" alt="" />;
        }
    }

    if (item.title) {
        message = item.title;
        if (item.type === 'post_like') { IconBadge = Heart; badgeColor = '#FF4444'; }
        else if (item.type === 'new_post') { IconBadge = Sparkles; badgeColor = '#0BD9E5'; }
        else if (item.type === 'new_confession') { IconBadge = Megaphone; badgeColor = '#0BD9E5'; }
        else if (item.type === 'global_anon_live') { IconBadge = Globe; badgeColor = '#BD00FF'; }
        
        if (item.body && item.body !== "Tap to view") subMessage = item.body;
    } else {
        if (item.type === 'campus_anon_live') { 
            message = 'Someone from your campus just entered anonymous chat!'; 
            subMessage = 'Join 100% anonymously · Tap to jump in 👀';
            senderName = 'Spark'; 
            IconBadge = Zap; 
            badgeColor = '#0BD9E5'; 
        }
        else if (item.type === 'like') { message = `${senderName} liked your shadow`; IconBadge = Heart; badgeColor = '#FF4444'; }
        else if (item.type === 'comment') { message = item.parentCommentId ? `${senderName} replied to your shadow` : `${senderName} commented on your shadow`; IconBadge = MessageCircle; badgeColor = '#4488FF'; }
        else if (item.type === 'comment_reply') { message = `${senderName} replied to your comment`; IconBadge = MessageCircle; badgeColor = '#4488FF'; }
        else if (item.type === 'new_follower') { message = `${senderName} started following you`; IconBadge = User; badgeColor = '#00D95A'; }
        else if (item.type === 'post_like') { message = `${senderName} liked your post`; IconBadge = Heart; badgeColor = '#FF4444'; }
        else if (item.type === 'post_comment') { message = item.parentCommentId ? `${senderName} replied to your post` : `${senderName} commented on your post`; IconBadge = MessageCircle; badgeColor = '#4488FF'; }
        else if (item.type === 'post_comment_reply') { message = `${senderName} replied to your comment`; IconBadge = MessageCircle; badgeColor = '#4488FF'; }
        else if (item.type === 'poll_vote') { message = `${senderName} voted on your poll`; IconBadge = BarChart2; badgeColor = '#0BD9E5'; }
        else if (item.type === 'new_follower_request') { message = `${senderName} requested to follow you`; IconBadge = User; badgeColor = '#0BD9E5'; }
        else if (item.type === 'request_accepted') { message = `${senderName} accepted your follow request`; IconBadge = User; badgeColor = '#0BD9E5'; }
        else if (item.type === 'shadow_mention') { message = `${senderName} mentioned you in a shadow`; IconBadge = Megaphone; badgeColor = '#0BD9E5'; }
        else if (item.type === 'shadow_comment_mention') { message = `${senderName} mentioned you in a comment`; IconBadge = Megaphone; badgeColor = '#0BD9E5'; }
        else { message = `${senderName} interacted with you`; IconBadge = Zap; badgeColor = '#0BD9E5'; }
        
        if (item.content) subMessage = `"${item.content.substring(0, 50)}${item.content.length > 50 ? '...' : ''}"`;
    }

    let timeAgo = 'Just now';
    if(item.createdAt) {
        const date = item.createdAt.toDate ? item.createdAt.toDate() : (new Date(item.createdAt.seconds ? item.createdAt.seconds * 1000 : item.createdAt));
        timeAgo = formatDistanceToNow(date, { addSuffix: true });
    }

    return (
        <div 
            onClick={() => onPress(item)}
            className={`flex items-center p-4 border-b border-[#161616] cursor-pointer hover:bg-white/5 transition-colors group ${!item.read && !item.isGlobal ? 'bg-[#0BD9E5]/5' : 'bg-transparent'}`}
        >
            <div className="relative mr-4 shrink-0">
                {AvatarComponent}
                <div className="absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-full border-2 border-[#0a0a0a] flex items-center justify-center" style={{ backgroundColor: badgeColor }}>
                    <IconBadge size={10} color="#FFF" strokeWidth={2.5} className={item.type.includes('like') ? "fill-white" : ""} />
                </div>
            </div>

            <div className="flex-1 min-w-0 pr-4">
                <p className={`text-[15px] truncate ${(!item.read && !item.isGlobal) ? 'font-bold text-white' : 'text-gray-300'}`}>
                    {message}
                </p>
                {subMessage && <p className="text-[13.5px] text-gray-500 italic truncate mb-0.5">{subMessage}</p>}
                <p className="text-xs text-gray-500">{timeAgo}</p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
                {PostPreviewComponent ? (
                    <div className="relative">
                        {PostPreviewComponent}
                        {!item.read && !item.isGlobal && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#0BD9E5] rounded-full border-[1.5px] border-[#0a0a0a]" />}
                    </div>
                ) : (
                    !item.read && !item.isGlobal && <div className="w-2 h-2 bg-[#0BD9E5] rounded-full" />
                )}
                
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item.id);
                    }}
                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                >
                    <Trash2 size={18} />
                </button>
            </div>
        </div>
    );
});

const globalNotificationsCache = {
  notifications: [],
  userProfiles: {},
  postData: {},
  lastVisible: null,
  hasMore: true,
  scrollY: 0,
  isInitialized: false,
};

export const NotificationsPage = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;
    const { identityMode } = useIdentity();

    const [notifications, setNotifications] = useState(globalNotificationsCache.notifications);
    const [userProfiles, setUserProfiles] = useState(globalNotificationsCache.userProfiles); 
    const [postData, setPostData] = useState(globalNotificationsCache.postData); 
    const [loading, setLoading] = useState(!globalNotificationsCache.isInitialized); 
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastVisible, setLastVisible] = useState(globalNotificationsCache.lastVisible);
    const [hasMore, setHasMore] = useState(globalNotificationsCache.hasMore);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const userProfilesRef = useRef(globalNotificationsCache.userProfiles);
    const postDataRef = useRef(globalNotificationsCache.postData);

    const BATCH_SIZE = 50;

    useEffect(() => {
        const cached = localStorage.getItem('activity_user_cache');
        if (cached) {
            const parsed = JSON.parse(cached);
            setUserProfiles(parsed);
            userProfilesRef.current = parsed;
        }
    }, []);

    const fetchProfilesForNotifications = async (notifs) => {
        const socialTypes = ['post_like', 'post_comment', 'post_comment_reply', 'new_follower', 'new_post', 'poll_vote', 'new_follower_request', 'request_accepted', 'post_mention', 'comment_mention'];
        const uidsToFetch = new Set();
        
        notifs.forEach(item => {
            if (socialTypes.includes(item.type) || (item.type === 'poll_vote' && !item.isConfession)) {
                const uid = item.fromUserId || item.senderId || item.userId;
                if (uid && !userProfilesRef.current[uid]) {
                    uidsToFetch.add(uid);
                }
            }
        });

        if (uidsToFetch.size === 0) return;

        const newProfiles = { ...userProfilesRef.current };
        const uidsArray = Array.from(uidsToFetch);
        
        for (let i = 0; i < uidsArray.length; i += 10) {
            const batchUids = uidsArray.slice(i, i + 10);
            try {
                const q = query(collection(db, 'users'), where('__name__', 'in', batchUids));
                const snap = await getDocs(q);
                snap.docs.forEach(docSnap => {
                    newProfiles[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
                });
            } catch (err) {
                console.error("Error fetching profiles batch:", err);
            }
        }

        userProfilesRef.current = newProfiles;
        setUserProfiles(newProfiles);
        try {
            localStorage.setItem('activity_user_cache', JSON.stringify(newProfiles));
        } catch (e) {}
    };

    const fetchPostDataForNotifications = async (notifs) => {
        const postTypes = ['post_like', 'post_comment', 'post_comment_reply', 'post_comment_nested', 'post_mention', 'comment_mention'];
        const postIdsToFetch = new Set();

        notifs.forEach(item => {
            if (postTypes.includes(item.type) && item.postId) {
                if (!postDataRef.current[item.postId]) {
                    postIdsToFetch.add(item.postId);
                }
            }
        });

        if (postIdsToFetch.size === 0) return;

        const newPostData = { ...postDataRef.current };
        const idsArray = Array.from(postIdsToFetch);

        for (let i = 0; i < idsArray.length; i += 10) {
            const batchIds = idsArray.slice(i, i + 10);
            try {
                const q = query(collection(db, 'posts'), where('__name__', 'in', batchIds));
                const snap = await getDocs(q);
                snap.docs.forEach(docSnap => {
                    newPostData[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
                });
            } catch (err) {
                console.error("Error fetching posts batch:", err);
            }
        }

        postDataRef.current = newPostData;
        setPostData(newPostData);
    };

    const fetchNotifications = useCallback(async (isLoadMore = false) => {
        if (!currentUser) return;

        try {
            if (!isLoadMore) setLoading(true);
            else setLoadingMore(true);

            const deletedIds = getDeletedIds();

            const notifRef = collection(db, 'users', currentUser.uid, 'notifications');
            let q;

            if (isLoadMore && lastVisible) {
                q = query(notifRef, orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(BATCH_SIZE));
            } else {
                q = query(notifRef, orderBy('createdAt', 'desc'), limit(BATCH_SIZE));
            }

            const snap = await getDocs(q);
            let notifList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            notifList = notifList.filter(item => !deletedIds.includes(item.id));

            if (notifList.length > 0) {
                setLastVisible(snap.docs[snap.docs.length - 1]);
            }
            
            if (snap.docs.length < BATCH_SIZE) {
                setHasMore(false);
            }

            if (isLoadMore) {
                setNotifications(prev => [...prev, ...notifList]);
            } else {
                setNotifications(notifList);
            }

            await fetchProfilesForNotifications(notifList);
            await fetchPostDataForNotifications(notifList);

            const unreadDocs = snap.docs.filter(docSnap => !docSnap.data().read);
            if (unreadDocs.length > 0) {
                const batch = writeBatch(db);
                unreadDocs.forEach(docSnap => {
                    batch.update(docSnap.ref, { read: true });
                });
                await batch.commit();
            }

        } catch (err) {
            console.error("Error fetching notifications:", err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [currentUser, lastVisible]);

    // Sync state into global cache
    useEffect(() => {
        if (notifications.length > 0) {
            globalNotificationsCache.notifications = notifications;
            globalNotificationsCache.userProfiles = userProfiles;
            globalNotificationsCache.postData = postData;
            globalNotificationsCache.lastVisible = lastVisible;
            globalNotificationsCache.hasMore = hasMore;
            globalNotificationsCache.isInitialized = true;
        }
    }, [notifications, userProfiles, postData, lastVisible, hasMore]);

    // Restore scroll position on mount
    useEffect(() => {
        if (globalNotificationsCache.scrollY > 0) {
            const savedY = globalNotificationsCache.scrollY;
            window.scrollTo(0, savedY);
            const timer = setTimeout(() => {
                window.scrollTo(0, savedY);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, []);

    // Track scroll position continuously
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                globalNotificationsCache.scrollY = window.scrollY;
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Initial fetch only when not cached
    useEffect(() => {
        if (!globalNotificationsCache.isInitialized && currentUser) {
            fetchNotifications(false);
        }
    }, [currentUser, fetchNotifications]);

    const displayNotifications = notifications.filter(n => {
        if (n.isGlobal) return true;
        const isShadowNotif = ['campus_anon_live', 'global_anon_live', 'like', 'comment', 'comment_reply', 'new_confession', 'shadow_mention', 'shadow_comment_mention'].includes(n.type) || (n.type === 'poll_vote' && n.isConfession);
        return identityMode === 'shadow' ? isShadowNotif : !isShadowNotif;
    });

    const markAllAsRead = async () => {
        const unreadNotifs = displayNotifications.filter(n => !n.read && !n.isGlobal);
        if (unreadNotifs.length === 0) return;

        const updated = notifications.map(n => 
            unreadNotifs.some(u => u.id === n.id) ? { ...n, read: true } : n
        );
        setNotifications(updated);

        try {
            const batch = writeBatch(db);
            unreadNotifs.slice(0, 499).forEach(notif => {
                const ref = doc(db, 'users', currentUser.uid, 'notifications', notif.id);
                batch.update(ref, { read: true });
            });
            await batch.commit();
        } catch (err) {}
    };

    const handleDeleteAll = async () => {
        setIsDeleting(true);
        try {
            const q = query(collection(db, 'users', currentUser.uid, 'notifications'), limit(500));
            const snapshot = await getDocs(q);
            const batch = writeBatch(db);
            let deleteCount = 0;
            snapshot.docs.forEach((docSnap) => {
                const data = docSnap.data();
                const isShadowNotif = ['campus_anon_live', 'global_anon_live', 'like', 'comment', 'comment_reply', 'new_confession', 'shadow_mention', 'shadow_comment_mention'].includes(data.type) || (data.type === 'poll_vote' && data.isConfession);
                const shouldDelete = identityMode === 'shadow' ? isShadowNotif : !isShadowNotif;
                if (shouldDelete) {
                    batch.delete(docSnap.ref);
                    deleteCount++;
                }
            });
            if (deleteCount > 0) await batch.commit();
            
            setNotifications(prev => prev.filter(n => {
                if (n.isGlobal) return true;
                const isShadowNotif = ['campus_anon_live', 'global_anon_live', 'like', 'comment', 'comment_reply', 'new_confession', 'shadow_mention', 'shadow_comment_mention'].includes(n.type) || (n.type === 'poll_vote' && n.isConfession);
                const isCurrentMode = identityMode === 'shadow' ? isShadowNotif : !isShadowNotif;
                return !isCurrentMode;
            }));
            setDeleteModalVisible(false);
        } catch (error) {
            toast.error("Could not delete notifications. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const deleteSingleNotification = useCallback(async (notifId) => {
        addDeletedId(notifId); 
        setNotifications(current => current.filter(n => n.id !== notifId));
        try {
            await deleteDoc(doc(db, 'users', currentUser.uid, 'notifications', notifId));
        } catch (err) {}
    }, [currentUser]);

    const handlePress = useCallback((item) => {
        if (item.type === 'campus_anon_live' || item.type === 'global_anon_live') navigate('/chat');
        else if (item.confessionId || item.type === 'like' || item.type === 'comment' || item.type === 'comment_reply' || item.type === 'new_confession' || (item.type === 'poll_vote' && item.isConfession)) {
            navigate(`/shadow/${item.confessionId || item.postId}`);
        } 
        else if (item.postId || item.type.startsWith('post_') || item.type === 'new_post' || (item.type === 'poll_vote' && !item.isConfession)) {
            // navigate(`/post/${item.postId}`); // Real posts not yet implemented
        } 

        if (!item.read && !item.isGlobal) {
            setNotifications(prev => prev.map(n => n.id === item.id ? { ...n, read: true } : n));
            const notifRef = doc(db, 'users', currentUser.uid, 'notifications', item.id);
            updateDoc(notifRef, { read: true }).catch(() => {});
        }
    }, [navigate, currentUser]);

    useEffect(() => {
        const handleGlobalScroll = () => {
            const container = document.getElementById('scrollable-container');
            const target = container || document.documentElement;
            const scrollTop = container ? container.scrollTop : window.scrollY;
            const scrollHeight = target.scrollHeight;
            const clientHeight = container ? container.clientHeight : window.innerHeight;
            
            if (scrollHeight - scrollTop - clientHeight < scrollHeight * 0.3) {
                if (hasMore && !loadingMore && !loading) {
                    fetchNotifications(true);
                }
            }
        };
        const target = document.getElementById('scrollable-container') || window;
        target.addEventListener('scroll', handleGlobalScroll);
        return () => target.removeEventListener('scroll', handleGlobalScroll);
    });

    useEffect(() => {
        if (!loading && !loadingMore && hasMore) {
            const timer = setTimeout(() => {
                const container = document.getElementById('scrollable-container');
                const target = container || document.documentElement;
                const clientHeight = container ? container.clientHeight : window.innerHeight;
                if (target.scrollHeight <= clientHeight + 100) {
                    fetchNotifications(true);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    });

    return (
        <main className="w-full sm:w-[700px] min-h-screen pb-20 sm:pb-0 border-r border-gray-800 flex flex-col shrink-0 bg-[#0a0a0a]">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center h-[60px] px-4 justify-between">
                <div className="flex items-center">
                    <button onClick={() => navigate('/')} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
                        <ChevronLeft size={24} className="text-white" />
                    </button>
                    <h1 className="text-xl font-bold text-white">Activity</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => setDeleteModalVisible(true)}
                        disabled={displayNotifications.filter(n => !n.isGlobal).length === 0}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                        <Trash2 size={22} className={displayNotifications.filter(n => !n.isGlobal).length > 0 ? "text-red-500" : "text-gray-500"} />
                    </button>
                    <button 
                        onClick={markAllAsRead}
                        disabled={!displayNotifications.some(n => !n.read && !n.isGlobal)}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                        <CheckCheck size={22} className={displayNotifications.some(n => !n.read && !n.isGlobal) ? "text-[#0BD9E5]" : "text-gray-500"} strokeWidth={2.5} />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
                    </div>
                ) : displayNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
                        <div className="w-20 h-20 rounded-full bg-[#161616] flex items-center justify-center mb-5">
                            <MessageCircle size={40} className="text-gray-600" />
                        </div>
                        <h2 className="text-white text-xl font-semibold mb-2">No activity yet</h2>
                        <p className="text-gray-500">Likes and comments will appear here</p>
                    </div>
                ) : (
                    <>
                        {displayNotifications.map(item => (
                            <NotificationRow 
                                key={item.id}
                                item={item}
                                userProfile={userProfiles[item.fromUserId]}
                                postData={postData[item.postId]}
                                onDelete={deleteSingleNotification}
                                onPress={handlePress}
                            />
                        ))}
                        {hasMore && (
                            <div className="p-6 flex justify-center">
                                {loadingMore ? (
                                    <div className="w-6 h-6 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
                                ) : (
                                    <div className="h-6" /> // spacer
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Delete Modal */}
            {deleteModalVisible && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#161616] border border-[#222] rounded-3xl p-6 max-w-sm w-full text-center">
                        <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={32} className="text-red-500" />
                        </div>
                        <h3 className="text-white text-xl font-bold mb-2">Clear All Activity?</h3>
                        <p className="text-gray-400 text-sm mb-6">This will permanently delete all your notifications.</p>
                        
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setDeleteModalVisible(false)}
                                disabled={isDeleting}
                                className="flex-1 py-3.5 rounded-full bg-[#222] text-white font-semibold hover:bg-[#333] transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleDeleteAll}
                                disabled={isDeleting}
                                className="flex-1 py-3.5 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 flex justify-center items-center"
                            >
                                {isDeleting ? <div className="w-5 h-5 rounded-full border-t-2 border-white animate-spin" /> : 'Delete All'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default NotificationsPage;
